
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ServiceImagesSection } from "./ServiceImagesSection";
import { ServiceInfoSection } from "./ServiceInfoSection";
import { ContactFormDialog } from "./ContactFormDialog";
import { AppointmentRequestDialog } from "./AppointmentRequestDialog";
import { type Service } from "./types";
import { Skeleton } from "@/components/ui/skeleton";
import { SimilarItems } from "./components/SimilarItems";

export const ServiceDetails = () => {
  const { id } = useParams();
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isAppointmentDialogOpen, setIsAppointmentDialogOpen] = useState(false);

  const { data: service, isLoading } = useQuery({
    queryKey: ["service", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("professional_services")
        .select(`
          *,
          profiles:professional_id (
            full_name,
            profile_photo,
            rating,
            response_rate,
            response_time,
            total_reviews,
            about_me,
            experience,
            education,
            languages,
            certifications
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Service;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Skeleton className="h-[400px] w-full rounded-xl" />
              <div className="grid grid-cols-4 gap-2">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-lg" />
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-5 w-1/4" />
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-[180px] w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <ServiceImagesSection service={service} />
          </div>
          <div>
            <ServiceInfoSection
              service={service}
              onContactClick={() => setIsContactDialogOpen(true)}
              onAppointmentClick={() => setIsAppointmentDialogOpen(true)}
            />
          </div>
        </div>
        
        {/* Similar Services Section */}
        <SimilarItems 
          type="service" 
          currentItemId={service.id} 
          category={service.category}
        />
      </main>

      <ContactFormDialog
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        serviceTitle={service.service_name}
        serviceId={service.id}
        professionalId={service.professional_id}
      />

      <AppointmentRequestDialog
        open={isAppointmentDialogOpen}
        onOpenChange={setIsAppointmentDialogOpen}
        serviceId={service.id}
        professionalId={service.professional_id}
        serviceName={service.service_name}
      />
    </div>
  );
};
